import { comparisonData } from '@/mocks/categories';

export default function ContentSections() {
  return (
    <>
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">
                The Problem
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Finding Quality Products Takes Too Much Time
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                When shopping through agent platforms, the product discovery process can feel overwhelming. 
                Thousands of listings spread across different pages, inconsistent product information, 
                and no easy way to compare items side by side. Many shoppers waste hours manually browsing 
                through endless product pages, only to lose track of what they found interesting.
              </p>
              <ul className="space-y-3">
                {[
                  'Too many products with poor organization across different sellers',
                  'Lack of consolidated information making comparisons difficult',
                  'No structured way to track and revisit interesting product finds',
                  'Missing QC photos and warehouse details scattered across messages',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground-600">
                    <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-close-line text-accent-600 text-xs"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Scattered%20collection%20of%20various%20fashion%20items%20including%20sneakers%2C%20hoodies%2C%20t-shirts%2C%20and%20accessories%20spread%20messily%20across%20a%20large%20wooden%20table%2C%20cluttered%20and%20chaotic%20arrangement%2C%20warm%20natural%20light%20highlighting%20the%20overwhelming%20abundance%20of%20unorganized%20products%2C%20cream%20wall%20background%2C%20editorial%20photography%20capturing%20the%20feeling%20of%20being%20overwhelmed%20by%20too%20many%20shopping%20choices&width=800&height=600&seq=problem-visual-v2&orientation=landscape"
                  alt="Disorganized product research without Basetao Spreadsheet"
                  title="Shopping without Basetao Spreadsheet organization"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautifully%20organized%20product%20curation%20scene%20with%20neatly%20arranged%20fashion%20items%20sorted%20into%20clear%20categories%20on%20a%20light%20oak%20surface%2C%20a%20tablet%20displaying%20a%20clean%20spreadsheet%20interface%20beside%20them%2C%20warm%20beige%20backdrop%2C%20soft%20daylight%20creating%20a%20calm%20and%20orderly%20atmosphere%2C%20editorial%20lifestyle%20photography%20showcasing%20systematic%20organization%20and%20clarity&width=800&height=600&seq=solution-visual-v2&orientation=landscape"
                  alt="Organized product research with Basetao Spreadsheet"
                  title="Efficient shopping with Basetao Spreadsheet"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                The Solution
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Basetao Spreadsheet Simplifies Product Discovery
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                The Basetao Spreadsheet transforms chaotic browsing into structured research. 
                Instead of jumping between dozens of tabs, you get a single organized view where 
                products are categorized, priced, and linked with their QC photos and warehouse details. 
                This approach turns hours of aimless searching into minutes of focused comparison.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Before', desc: 'Random searching across endless pages', icon: 'ri-emotion-unhappy-line', color: 'accent' },
                  { label: 'After', desc: 'Organized spreadsheet research in one view', icon: 'ri-emotion-happy-line', color: 'primary' },
                ].map((item) => (
                  <div key={item.label} className={`p-4 rounded-xl bg-${item.color}-50 border border-${item.color}-100`}>
                    <div className={`w-8 h-8 rounded-lg bg-${item.color}-100 flex items-center justify-center mb-2`}>
                      <i className={`${item.icon} text-${item.color}-600 text-sm`}></i>
                    </div>
                    <p className="text-xs font-semibold text-foreground-800 mb-1">{item.label}</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
              Comparison
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Traditional Searching vs Basetao Spreadsheet
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto text-sm">
              See how the Basetao Spreadsheet approach compares to traditional product browsing
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-background-300/60">
                  <th className="text-left py-4 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Feature</th>
                  <th className="text-left py-4 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-close-circle-line text-accent-500"></i>
                      Traditional Searching
                    </span>
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line text-primary-500"></i>
                      Basetao Spreadsheet
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-background-200/50 ${i % 2 === 0 ? 'bg-background-50/50' : ''}`}>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${row.icon} text-foreground-600 text-xs`}></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-800">{row.feature}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-sm text-foreground-500">{row.traditional}</td>
                    <td className="py-3.5 px-4 text-sm text-primary-700 font-medium">{row.basetao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              Research Hub
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Basetao Spreadsheet In-Depth Resources
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto text-sm">
              Everything you need to master the Basetao Spreadsheet and become a smarter shopper
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ResourceCard
              icon="ri-guide-line"
              title="How to Use the Basetao Spreadsheet"
              desc="A step-by-step walkthrough covering spreadsheet layout, filtering techniques, and how to extract maximum value from every column and row."
              color="primary"
            />
            <ResourceCard
              icon="ri-truck-line"
              title="Basetao Shipping Cost Explained"
              desc="Understand how shipping costs are calculated, what factors influence pricing, and how to estimate your total delivery expenses before ordering."
              color="accent"
            />
            <ResourceCard
              icon="ri-shield-check-line"
              title="Is Basetao Legit? Our Honest Review"
              desc="An unbiased look at platform safety, buyer protection measures, payment security, and what real users experience when shopping through Basetao."
              color="secondary"
            />
            <ResourceCard
              icon="ri-store-2-line"
              title="How the Basetao Warehouse Works"
              desc="Behind the scenes look at warehouse operations, how products are received and stored, and what happens between ordering and shipping."
              color="accent"
            />
            <ResourceCard
              icon="ri-coupon-line"
              title="Basetao Coupon & Discount Codes"
              desc="Stay updated on the latest promotional offers, discount strategies, and ways to save money when using the Basetao Spreadsheet for shopping."
              color="secondary"
            />
            <ResourceCard
              icon="ri-database-2-line"
              title="Product Spreadsheet Database Guide"
              desc="Explore how product data is organized across different categories, from shoes and clothing to accessories and lifestyle items."
              color="primary"
            />
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">
              Trust & Safety
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Your Safety Matters When Shopping
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto text-sm">
              The Basetao Spreadsheet helps you shop smarter, but knowing how to stay safe is equally important
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: 'ri-lock-line', title: 'Secure Payments', desc: 'Always use trusted payment gateways and avoid direct transfers to unknown sellers. The Basetao Spreadsheet links only to verified product listings.' },
              { icon: 'ri-eye-line', title: 'QC Photo Verification', desc: 'Learn how to read QC photos properly and identify potential issues before your items are shipped from the warehouse.' },
              { icon: 'ri-shield-flash-line', title: 'Fraud Prevention', desc: 'Recognize common shopping scams and protect yourself. Our guides help you spot red flags in product listings and seller behavior.' },
              { icon: 'ri-user-heart-line', title: 'Community Insights', desc: 'Tap into real user experiences and reviews. The Basetao Spreadsheet community shares honest feedback about products and sellers.' },
            ].map((item) => (
              <div key={item.title} className="p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-primary-600 text-lg`}></i>
                </div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-2">{item.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ResourceCard({ icon, title, desc, color }: {
  icon: string;
  title: string;
  desc: string;
  color: 'primary' | 'accent' | 'secondary';
}) {
  return (
    <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-background-300/60 transition-all duration-200 cursor-pointer group">
      <div className={`w-10 h-10 rounded-lg bg-${color}-50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
        <i className={`${icon} text-${color}-600 text-lg`}></i>
      </div>
      <h3 className="font-semibold text-foreground-900 text-sm mb-2">{title}</h3>
      <p className="text-xs text-foreground-500 leading-relaxed">{desc}</p>
    </div>
  );
}